'use strict';

const HEX = '0123456789abcdef';

/** RFC4122-ish v4 id, falling back to Math.random when crypto is unavailable. */
function uuid() {
  const c = window.crypto || window.msCrypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && c.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    out += HEX[bytes[i] >> 4] + HEX[bytes[i] & 15];
    if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
  }
  return out;
}

const now = () => new Date().toISOString();

function parseQuery(search) {
  const out = {};
  const raw = String(search || '').replace(/^\?/, '').split('#')[0];
  if (!raw) return out;
  raw.split('&').forEach((pair) => {
    if (!pair) return;
    const idx = pair.indexOf('=');
    const key = idx === -1 ? pair : pair.slice(0, idx);
    const val = idx === -1 ? '' : pair.slice(idx + 1);
    try { out[decodeURIComponent(key).toLowerCase()] = decodeURIComponent(val.replace(/\+/g, ' ')); }
    catch (_) { out[key.toLowerCase()] = val; }
  });
  return out;
}

function hasAnyParam(search, names) {
  const query = parseQuery(search);
  for (let i = 0; i < names.length; i += 1) {
    const value = query[String(names[i]).toLowerCase()];
    if (typeof value === 'string' && value !== '') return true;
  }
  return false;
}

function throttle(fn, wait) {
  let last = 0;
  let timer = null;
  return function throttled() {
    const args = arguments;
    const remaining = wait - (Date.now() - last);
    if (remaining <= 0) {
      last = Date.now();
      fn.apply(null, args);
    } else if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        last = Date.now();
        fn.apply(null, args);
      }, remaining);
    }
  };
}

const safe = (fn) => function guarded() {
  try { return fn.apply(this, arguments); } catch (_) { return undefined; }
};

// Keep in sync with apps/api/src/domain/urlScrub.js (server re-scrubs anyway).
const SENSITIVE_PARAMS = [
  'access_token', 'id_token', 'refresh_token', 'token', 'auth_token', 'oauth_token',
  'oauth_verifier', 'code', 'client_secret', 'password', 'passwd', 'pwd', 'secret',
  'api_key', 'apikey', 'session_state', 'authorization', 'jwt',
];

function paramKey(pair) {
  const eq = pair.indexOf('=');
  const raw = eq === -1 ? pair : pair.slice(0, eq);
  try { return decodeURIComponent(raw).trim().toLowerCase(); } catch (_) { return raw.trim().toLowerCase(); }
}

const isSensitivePair = (pair) => pair && SENSITIVE_PARAMS.indexOf(paramKey(pair)) !== -1;
const scrubQuery = (query) => query.split('&').filter((pair) => pair && !isSensitivePair(pair)).join('&');

/**
 * Strip credentials (OAuth implicit-flow tokens, auth codes, passwords) from a
 * URL before it leaves the browser. Hash routes like `#/casino` are kept.
 */
function scrubUrl(url) {
  if (!url) return url;
  const value = String(url);
  const hashIdx = value.indexOf('#');
  let base = hashIdx === -1 ? value : value.slice(0, hashIdx);
  let fragment = hashIdx === -1 ? null : value.slice(hashIdx + 1);

  const qIdx = base.indexOf('?');
  if (qIdx !== -1) {
    const query = scrubQuery(base.slice(qIdx + 1));
    base = base.slice(0, qIdx) + (query ? '?' + query : '');
  }

  if (fragment !== null) {
    const isRoute = /^!?\//.test(fragment);
    const routeQueryIdx = fragment.indexOf('?');
    if (isRoute && routeQueryIdx !== -1) {
      const query = scrubQuery(fragment.slice(routeQueryIdx + 1));
      fragment = fragment.slice(0, routeQueryIdx) + (query ? '?' + query : '');
    } else if (!isRoute && fragment.indexOf('=') !== -1
      && fragment.replace(/^\?/, '').split('&').some(isSensitivePair)) {
      fragment = null;
    }
  }

  return fragment ? base + '#' + fragment : base;
}

const clean = (obj) => {
  const out = {};
  Object.keys(obj || {}).forEach((key) => {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') out[key] = obj[key];
  });
  return out;
};

module.exports = { uuid, now, parseQuery, hasAnyParam, throttle, safe, clean, scrubUrl };
